import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * The look every text field and select shares (spec-64-u2, U-150 / U-202): one height
 * (32 px, 44 px on a phone), one edge in `--control-border` (3.8:1, WCAG 1.4.11), a white
 * fill, and a red edge when invalid. Callers may set a width; they never restyle it.
 */
export const CONTROL_CLASS =
  'h-8 w-full min-w-0 rounded-lg border border-control-border bg-card px-2.5 text-base text-foreground transition-colors placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground aria-invalid:border-destructive max-md:h-11 md:text-sm'

interface FieldControl {
  id: string
  describedBy: string | undefined
  invalid: boolean
  required: boolean
}

const FieldContext = React.createContext<FieldControl | null>(null)

type ControlProps = {
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: React.AriaAttributes['aria-invalid']
  'aria-required'?: React.AriaAttributes['aria-required']
}

/**
 * The props a control inside a `FormField` takes from it: its id (the field's, so the label
 * always points at it), the hint and error it is described by (after the caller's own
 * description), and `aria-invalid`/`aria-required` unless the caller sets them. Outside a
 * `FormField` the props pass through unchanged.
 */
export function useFieldProps<P extends ControlProps>(props: P): P {
  const field = React.useContext(FieldContext)
  if (!field) return props
  const describedBy = [field.describedBy, props['aria-describedby']].filter(Boolean).join(' ')
  return {
    ...props,
    id: field.id,
    'aria-describedby': describedBy || undefined,
    'aria-invalid': props['aria-invalid'] ?? (field.invalid || undefined),
    'aria-required': props['aria-required'] ?? (field.required || undefined),
  }
}

export interface FormFieldProps {
  label: React.ReactNode
  /** Marks the label "(verplicht)" and the control `aria-required`. */
  required?: boolean
  /** One line under the control. */
  hint?: React.ReactNode
  /** The error of this field; announced (`role="alert"`) and tied to the control. */
  error?: React.ReactNode
  /** One `Input`, `NativeSelect`, `Textarea` or other control that uses `useFieldProps`. */
  children: React.ReactNode
  className?: string
}

/**
 * A labelled form field (spec-64-u2, U-150 / IA-14): the label, "(verplicht)" when
 * required, the control, a hint and the error. The control gets its id and description
 * from here, so a screen reader hears the label, the hint and the error with it.
 */
export function FormField({ label, required = false, hint, error, children, className }: FormFieldProps) {
  const id = React.useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined
  const control = React.useMemo<FieldControl>(
    () => ({ id, describedBy, invalid: Boolean(error), required }),
    [id, describedBy, error, required],
  )
  return (
    <div data-slot="form-field" className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={id} className="text-sm font-semibold text-foreground">
        {label}
        {required ? (
          <>
            {' '}
            <span className="font-normal text-muted-foreground">(verplicht)</span>
          </>
        ) : null}
      </label>
      <FieldContext.Provider value={control}>{children}</FieldContext.Provider>
      {hint ? (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  )
}
